import type { LucideIcon } from 'lucide-react'
import {
  CalendarClockIcon,
  CreditCardIcon,
  LockIcon,
  MegaphoneIcon,
  PlugIcon,
  ShoppingCartIcon,
  TicketIcon,
  WorkflowIcon,
} from 'lucide-react'
import type { ComparisonRow, Faq, PaymentStage } from '../types/content'

export interface Integration {
  name: string
  description: string
  icon: LucideIcon
}

export const includedInEveryPlan: string[] = [
  'Brand and visual direction',
  'Custom UI design',
  'Responsive design',
  'CMS setup',
  'Basic SEO',
  'Analytics',
  'Performance optimization',
  'Accessibility basics',
  'Feedback rounds agreed in the Scope of Work',
  'Launch support',
  '30-day post-launch support',
]

export const integrations: Integration[] = [
  {
    name: 'E-Commerce',
    description:
      'Product catalogue, shopping cart, checkout flows, inventory tracking, and customer order management.',
    icon: ShoppingCartIcon,
  },
  {
    name: 'Booking & Appointments',
    description:
      'Interactive booking calendars, time-slot management, automated confirmations, and SMS/email reminders.',
    icon: CalendarClockIcon,
  },
  {
    name: 'Events & Ticketing',
    description:
      'Event listings, digital ticket generation, RSVP management, and attendee check-in systems.',
    icon: TicketIcon,
  },
  {
    name: 'Payment Gateways',
    description:
      'Secure gateway integrations (Razorpay, Stripe, UPI, CCAvenue) with automatic invoice generation.',
    icon: CreditCardIcon,
  },
  {
    name: 'CRM & Marketing Tools',
    description:
      'Lead capture routing, email marketing platforms (Mailchimp, Klaviyo), WhatsApp automation, and analytics.',
    icon: MegaphoneIcon,
  },
  {
    name: 'Automations & Workflows',
    description:
      'Automated form routing, internal team Slack/Email notifications, and multi-app webhook connections.',
    icon: WorkflowIcon,
  },
  {
    name: 'Third-Party APIs',
    description:
      'Custom REST API integrations, external database syncing, ERP feeds, and bespoke web services.',
    icon: PlugIcon,
  },
  {
    name: 'Membership & Gated Content',
    description:
      'Client account portals, authentication systems, subscription gating, and protected media libraries.',
    icon: LockIcon,
  },
]

export const integrationPricing = {
  price: 'From ₹ 5,000 / per integration',
  note: 'Final cost is confirmed based on the exact functionality, platform, and complexity required, and is agreed in writing in the Scope of Work before work begins. Integrations are fully supported and can be incorporated at any stage of the project or after initial launch.',
}

export const pageDefinition = {
  counts: [
    'A unique layout with its own content and structure',
    'A landing page for a campaign or product',
    'A CMS template — one blog template covers every post',
    'A form or booking flow with its own screens',
  ],
  doesNotCount: [
    'A blog post, case study or job listing published into an existing template',
    'Legal pages such as privacy or terms',
    'A thank-you or confirmation screen',
    'Mobile layouts — every page is responsive by default',
  ],
}

export const comparisonRows: ComparisonRow[] = [
  { label: 'Pages included', values: ['1–3', '4–6', '7+'] },
  { label: 'Price', values: ['₹ 15,000', '₹ 25,000', 'Quoted on Scope'] },
  { label: 'Timeline', values: ['3 weeks', '3–4 weeks', 'Quoted'] },
  { label: 'Full service stack', values: ['Included', 'Included', 'Included'] },
  {
    label: 'Revision rounds',
    values: ['Agreed in Scope of Work', 'Agreed in Scope of Work', 'Agreed in Scope of Work'],
  },
  { label: 'CMS templates', values: ['1', 'Up to 3', 'Unlimited'] },
  { label: 'Content migration', values: ['—', 'Up to 20 items', 'Full migration'] },
  { label: 'E-commerce', values: ['Add-on', 'Add-on', 'Available in scope'] },
  { label: 'Motion and interaction', values: ['Add-on', 'Add-on', 'Available in scope'] },
  { label: 'CMS training session', values: ['Recorded walkthrough', '60 minutes, live', 'Team session'] },
  { label: 'Post-launch support', values: ['30 days', '30 days', '30 days'] },
]

export const paymentStages: PaymentStage[] = [
  {
    share: '50%',
    title: 'On signing',
    description:
      'Paid before we start. It books your slot in the schedule and covers the strategy and design phase.',
  },
  {
    share: '50%',
    title: 'On launch',
    description:
      'Paid when the site goes live, not thirty days later. No milestone invoices in between.',
  },
]

export const pricingFaqs: Faq[] = [
  {
    question: 'What happens if the scope changes mid-project?',
    answer:
      'We stop and re-quote in writing before anyone does any work. The new number is fixed too, and you can decline it and finish the original scope instead. Nothing is ever billed as a surprise at the end.',
  },
  {
    question: 'Why is Custom quoted rather than priced?',
    answer:
      'Past six or seven pages the cost is driven by structure rather than page count — how many CMS templates, how much content migrates, whether there is a store. We scope it on a call and send one fixed number.',
  },
  {
    question: 'How many rounds of feedback do I get?',
    answer:
      'The number of feedback rounds is agreed in the Scope of Work before the project starts, so it is fixed and in writing from day one. Feedback is collected in one consolidated pass per round, which is faster for you and produces better decisions than a stream of separate notes.',
  },
  {
    question: 'Is the price really fixed?',
    answer:
      'Yes. We do not bill hourly and we do not add fees for going over. The only way the number changes is if you ask for work outside the agreed scope, and then it changes by written agreement first.',
  },
  {
    question: 'What do you need from us?',
    answer:
      'A single decision-maker, roughly four hours across the project for calls and reviews, and your content or a decision to have us write it. Projects only slip when reviews sit unanswered.',
  },
  {
    question: 'Do you offer payment plans?',
    answer:
      'For Custom engagements we can split the second half into two payments across the build. For Starter and Standard the two-payment structure already keeps the upfront commitment small.',
  },
  {
    question: 'What is covered by 30-day post-launch support?',
    answer:
      'Anything broken, plus small content and layout corrections. It does not cover new pages or new features — those become a retainer or a separate quote.',
  },
]
